import { useRef, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as THREE from 'three';
import {
  Github,
  Linkedin,
  MessageCircle,
  Mail,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Menu,
  X,
} from 'lucide-react';
import { useProfile } from '../hooks/useprofile';
import { useEducation } from '../hooks/useeducation';
import { useExperience } from '../hooks/useexperience';
import { useInvolvements } from '../hooks/useinvolvements';
import { useAwards } from '../hooks/useawards';
import { useVisitorCount } from '../context/VisitorCounterContext';

const Home = () => {
  const mountRef = useRef(null);
  const videoRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [showCV, setShowCV] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch dynamic data
  const { profile, loading: profileLoading } = useProfile();
  const { education, loading: educationLoading } = useEducation();
  const { experience, loading: experienceLoading } = useExperience();
  const { involvements, loading: involvementsLoading } = useInvolvements();
  const { awards, loading: awardsLoading } = useAwards();
  const { visitorCount, loading: visitorLoading } = useVisitorCount();

  // Handle scroll button visibility and direction
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const isScrollable = scrollHeight > clientHeight;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;

      setShowScrollButton(isScrollable);
      setIsAtBottom(isNearBottom);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      handleScroll();
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [showCV, educationLoading, experienceLoading, involvementsLoading, awardsLoading]);

  // Three.js particle animation
  useEffect(() => {
    if (!mountRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    mountRef.current.appendChild(renderer.domElement);

    const isMobile = window.innerWidth < 768;
    const particleCount = isMobile ? 50 : 300;
    const particlesGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount * 3; i++) {
      positions[i] = (Math.random() - 0.5) * 8;
    }

    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

const createGlowTexture = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  
  // Create radial gradient for glow effect
  const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(0.2, 'rgba(255, 255, 255, 0.8)');
  gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.3)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 64, 64);
  
  return new THREE.CanvasTexture(canvas);
};

const glowTexture = createGlowTexture();

const particlesMaterial = new THREE.PointsMaterial({
      size: isMobile ? 0.04: 0.03,
      color: 0xffffff,
      transparent: true,
      opacity: 1,
      map: glowTexture,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const particles = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particles);

    camera.position.z = 3;

    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      particles.rotation.y += 0.001;
      particles.rotation.x += 0.0005;
      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      particlesGeometry.dispose();
      particlesMaterial.dispose();
      renderer.dispose();
      if (mountRef.current) {
        mountRef.current.innerHTML = '';
      }
    };
  }, []);

  const toggleVideo = () => {
    if (!videoRef.current) return;
    if (isVideoPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsVideoPlaying(p => !p);
  };

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    
    if (isAtBottom) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      scrollContainerRef.current.scrollBy({
        top: 300,
        behavior: 'smooth'
      });
    }
  };

  const navItem = (label, path) => {
    const active = location.pathname === path;
    return (
      <button
        onClick={() => {
          navigate(path);
          setMobileMenuOpen(false);
        }}
        className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${active
          ? 'bg-white/10 text-white'
          : 'text-white/60 hover:text-white hover:bg-white/5'
          }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="relative h-screen bg-black text-white overflow-hidden font-mono flex flex-col">
      {/* VIDEO COMMENTED OUT - Just black background */}
      {/* <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        className="fixed inset-0 w-full h-full object-cover z-0 opacity-20"
      >
        <source src="/bgvideo.mp4" type="video/mp4" />
      </video> */}

      {/* Video Control Button - COMMENTED OUT */}
      {/* <button
        onClick={toggleVideo}
        aria-label={isVideoPlaying ? 'Pause background video' : 'Play background video'}
        className="fixed top-4 right-4 md:top-6 md:right-6 z-50 p-2 text-white/60 hover:text-white transition-colors"
      >
        {isVideoPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </button> */}

      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="fixed top-4 left-4 z-50 p-2 text-white/60 hover:text-white transition-colors lg:hidden"
        aria-label="Toggle menu"
      >
        {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/95 z-40 lg:hidden">
          <div className="flex flex-col items-center justify-center h-full space-y-8 px-6">
            <img
              src="profile.jpg"
              alt="Aakash Poudel"
              className="w-32 h-32 rounded-full border border-white/20 object-cover"
            />
            
            <h2 className="text-2xl font-light">Aakash Poudel</h2>
            
            <div className="flex items-center gap-4">
              <a href="https://github.com/aakash2060" className="text-white/60 hover:text-white transition-colors">
                <Github className="w-5 h-5" />
              </a>
              <a href="mailto:aakash.poudel.900@gmail.com" className="text-white/60 hover:text-white transition-colors">
                <Mail className="w-5 h-5" />
              </a>
              <a href="https://www.linkedin.com/in/aakashpoudel900/" className="text-white/60 hover:text-white transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://discord.com/users/1206639502370807878" className="text-white/60 hover:text-white transition-colors">
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>

            <nav className="flex flex-col items-center gap-4 pt-4">
              <button
                onClick={() => {
                  setShowCV(!showCV);
                  setMobileMenuOpen(false);
                }}
                className="text-white/60 hover:text-white transition-colors text-lg"
              >
                {showCV ? 'About Me' : 'Curriculum Vitae'}
              </button>
              
              <button
                onClick={() => {
                  navigate('/projects');
                  setMobileMenuOpen(false);
                }}
                className="text-white/60 hover:text-white transition-colors text-lg"
              >
                Projects
              </button>
              
              <button
                onClick={() => {
                  navigate('/research');
                  setMobileMenuOpen(false);
                }}
                className="text-white/60 hover:text-white transition-colors text-lg"
              >
                Research
              </button>
              
              <button
                onClick={() => {
                  navigate('/writings');
                  setMobileMenuOpen(false);
                }}
                className="text-white/60 hover:text-white transition-colors text-lg"
              >
                Writings
              </button>
            </nav>
          </div>
        </div>
      )}

      <div ref={mountRef} className="fixed inset-0 pointer-events-none z-10" />

      {/* Main Container */}
      <div className="relative z-20 flex-grow flex overflow-hidden">
        <div className="w-full mx-auto px-4 sm:px-6 py-6 sm:py-8 lg:py-12 flex flex-col lg:flex-row gap-8 lg:gap-24 max-w-7xl">
          
          {/* Left Sidebar - Hidden on mobile, shown on desktop */}
          <aside className="hidden lg:flex w-[280px] xl:w-[300px] flex-shrink-0 items-center">
            <div className="flex flex-col items-center space-y-8 w-full">
              <img
                src="profile.jpg"
                alt="Aakash Poudel"
                loading="lazy"
                width="256"
                height="256"
                className="w-48 xl:w-64 h-48 xl:h-64 rounded-full border border-white/20 object-cover"
              />

              <h1 className="text-3xl xl:text-4xl font-light tracking-tight text-center">Aakash Poudel</h1>

              <div className="flex items-center justify-center gap-5">
                <a href="https://github.com/aakash2060" className="text-white/60 hover:text-white transition-colors" aria-label="GitHub">
                  <Github className="w-5 h-5" />
                </a>
                <a href="mailto:aakash.poudel.900@gmail.com" className="text-white/60 hover:text-white transition-colors" aria-label="Email">
                  <Mail className="w-5 h-5" />
                </a>
                <a href="https://www.linkedin.com/in/aakashpoudel900/"
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="https://discord.com/users/1206639502370807878"
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="Discord"
                >
                  <MessageCircle className="w-5 h-5" />
                </a>
              </div>

              <nav className="flex flex-col gap-2 w-full pt-4">
                <button
                  onClick={() => setShowCV(!showCV)}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${showCV
                    ? 'bg-white/10 text-white'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <span>{showCV ? 'About Me' : 'Curriculum Vitae'}</span>
                </button>

                {navItem('Projects', '/projects')}
                {navItem('Research', '/research')}
                {navItem('Writings', '/writings')}
              </nav>
            </div>
          </aside>

          {/* Mobile Header - Only visible on mobile */}
          <div className="lg:hidden text-center pt-12 pb-4">
            <img
              src="profile.jpg"
              alt="Aakash Poudel"
              className="w-32 h-32 rounded-full border border-white/20 object-cover mx-auto mb-4"
            />
            <h1 className="text-2xl sm:text-3xl font-light">Aakash Poudel</h1>
          </div>

          {/* Right Content Area - SCROLLABLE - NO HORIZONTAL SCROLL */}
          <main 
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide relative" 
            style={{ maxHeight: 'calc(100vh - 8rem)' }}
          >
            <div className="w-full max-w-full pt-0 lg:pt-8 pb-8 overflow-x-hidden">
              {!showCV ? (
                <section className="max-w-2xl text-white/90 text-base sm:text-lg leading-[1.8] space-y-4 sm:space-y-5 overflow-x-hidden">
                  {profileLoading ? (
                    <div className="text-white/40">Loading profile...</div>
                  ) : profile ? (
                    <>
                      {profile.paragraph1 && <p>{profile.paragraph1}</p>}
                      {profile.paragraph2 && <p>{profile.paragraph2}</p>}
                      {profile.paragraph3 && <p>{profile.paragraph3}</p>}
                    </>
                  ) : (
                    <p className="text-white/40">No profile information available.</p>
                  )}
                </section>
              ) : (
                <div className="space-y-12 sm:space-y-16 pb-12 overflow-x-hidden max-w-2xl">
                  {/* Education Section */}
                  <section className="overflow-x-hidden">
                    <h2 className="text-white/40 text-xs sm:text-sm uppercase tracking-wide mb-6 sm:mb-8">Education</h2>
                    {educationLoading ? (
                      <div className="text-white/40">Loading education...</div>
                    ) : education.length > 0 ? (
                      <div className="space-y-6 sm:space-y-8 max-w-full overflow-x-hidden">
                        {education.map((edu) => (
                          <div key={edu.id} className="flex flex-col sm:flex-row gap-4 sm:gap-5 min-w-0">
                            {edu.image && (
                              <img
                                src={edu.image}
                                alt={edu.school}
                                loading="lazy"
                                width="64"
                                height="64"
                                className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg border border-white/10 object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg sm:text-xl font-medium text-white mb-1 break-words">{edu.degree}</h3>
                              <div className="text-white/70 text-sm sm:text-base mb-2 break-words">{edu.school}</div>
                              <div className="text-white/40 text-xs sm:text-sm break-words">
                                {edu.period}{edu.gpa && ` • GPA ${edu.gpa}`}
                              </div>
                              {edu.achievements && (
                                <div className="text-white/50 text-xs sm:text-sm mt-2 break-words">
                                  {edu.achievements}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-white/40">No education information available.</div>
                    )}
                  </section>

                  {/* Experience Section */}
                  <section className="overflow-x-hidden">
                    <h2 className="text-white/40 text-xs sm:text-sm uppercase tracking-wide mb-6 sm:mb-8">Experience</h2>
                    {experienceLoading ? (
                      <div className="text-white/40">Loading experience...</div>
                    ) : experience.length > 0 ? (
                      <div className="space-y-6 sm:space-y-8 max-w-full overflow-x-hidden">
                        {experience.map((exp) => (
                          <div key={exp.id} className="flex flex-col sm:flex-row gap-4 sm:gap-5 min-w-0">
                            {exp.image && (
                              <img
                                src={exp.image}
                                alt={exp.company}
                                loading="lazy"
                                width="64"
                                height="64"
                                className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg border border-white/10 object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg sm:text-xl font-medium text-white mb-1 break-words">{exp.role}</h3>
                              <div className="text-white/70 text-sm sm:text-base mb-2 break-words">{exp.company}</div>
                              <div className="text-white/40 text-xs sm:text-sm mb-3 break-words">{exp.period}</div>
                              {exp.description && (
                                <p className="text-white/60 text-xs sm:text-sm leading-relaxed break-words">
                                  {exp.description}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-white/40">No experience information available.</div>
                    )}
                  </section>

                  {/* Involvements Section */}
                  <section className="overflow-x-hidden">
                    <h2 className="text-white/40 text-xs sm:text-sm uppercase tracking-wide mb-6 sm:mb-8">Involvements</h2>
                    {involvementsLoading ? (
                      <div className="text-white/40">Loading involvements...</div>
                    ) : involvements.length > 0 ? (
                      <div className="space-y-6 sm:space-y-8 max-w-full overflow-x-hidden">
                        {involvements.map((involvement) => (
                          <div key={involvement.id} className="flex flex-col sm:flex-row gap-4 sm:gap-5 min-w-0">
                            {involvement.image && (
                              <img
                                src={involvement.image}
                                alt={involvement.organization}
                                loading="lazy"
                                width="64"
                                height="64"
                                className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg border border-white/10 object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg sm:text-xl font-medium text-white mb-1 break-words">{involvement.role}</h3>
                              <div className="text-white/70 text-sm sm:text-base mb-2 break-words">{involvement.organization}</div>
                              {involvement.institution && (
                                <div className="text-white/60 text-xs sm:text-sm mb-2 break-words">{involvement.institution}</div>
                              )}
                              <div className="text-white/40 text-xs sm:text-sm break-words">{involvement.period}</div>
                              {involvement.description && (
                                <p className="text-white/60 text-xs sm:text-sm leading-relaxed mt-2 break-words">
                                  {involvement.description}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-white/40">No involvements information available.</div>
                    )}
                  </section>

                  {/* Awards & Recognition Section */}
                  <section className="overflow-x-hidden">
                    <h2 className="text-white/40 text-xs sm:text-sm uppercase tracking-wide mb-6 sm:mb-8">Awards & Recognition</h2>
                    {awardsLoading ? (
                      <div className="text-white/40">Loading awards...</div>
                    ) : awards.length > 0 ? (
                      <div className="space-y-2">
                        {awards.map((award) => (
                          <a
                            key={award.id}
                            href={award.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center justify-between py-2 text-white/90 hover:text-white transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <span className="text-white/60 mr-2">•</span>
                              <span className="text-sm sm:text-base">{award.title}</span>
                              <span className="text-white/40 ml-2 text-xs sm:text-sm">• {award.date}</span>
                            </div>
                            <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors flex-shrink-0 ml-2" />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div className="text-white/40">No awards information available.</div>
                    )}
                  </section>
                </div>
              )}
            </div>

            {/* Scroll Button - Desktop Only */}
            {showScrollButton && (
              <button
                onClick={handleScroll}
                className="hidden lg:block fixed right-40 top-1/2 -translate-y-3/4 z-30 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-all"
                aria-label={isAtBottom ? "Scroll to top" : "Scroll down"}
              >
                {isAtBottom ? (
                  <ChevronUp className="w-6 h-6 text-white/60" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-white/60" />
                )}
              </button>
            )}
          </main>
        </div>
      </div>

<footer className="relative z-20 border-t border-white/10 bg-black/60 backdrop-blur-md">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
    
    <div className="flex flex-col sm:flex-row items-center justify-between text-white/60 text-sm gap-4">

      {/* Left — Visitor Count */}
      {!visitorLoading && (
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-white/40 rounded-full" />
          <span>Site Visitors  :</span>
          <span className="text-white font-medium">
            {visitorCount.toLocaleString()}
          </span>
        </div>
      )}

      {/* Center — Get in Touch */}
      <p className="text-center whitespace-nowrap">
        Get in touch: <span className="text-white/90">aakash.poudel.900@gmail.com</span>
      </p>

      {/* Right — Copyright */}
      <p className="whitespace-nowrap">
        © 2024 - {new Date().getFullYear()} 
      </p>

    </div>

  </div>
</footer>




      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default Home;